/**
 * "How memos are written" (DOP-620): the recipe every memo on this workflow
 * is written to, in plain words.
 *
 * Left column: what goes in every memo, in order, as briefs you open one at
 * a time, then the sections only certain deals get. Right column: the voice
 * (tone, DOP-514). Below: the company facts and financial figures, and how
 * much each matters; the years the financial table shows (DOP-512) sit with
 * the figures they window (DOP-660).
 *
 * Every save goes through `recipe_edits`. The server merges it and bumps the
 * recipe version, and the page shows what it stored.
 */

import React, { useCallback, useEffect, useId, useState } from "react";

import type {
	DealScreeningRecipeEdits,
	DealScreeningWorkflow,
} from "../../types/dealScreening";
import RecipePolicyTables from "./RecipePolicyTables";
import SectionBriefCard from "./SectionBriefCard";

export interface DealScreeningSettingsProps {
	workflow: DealScreeningWorkflow;
	/** Resolves true when stored; reports its own failure and resolves false. */
	onSaveRecipe: (edits: DealScreeningRecipeEdits) => Promise<boolean>;
}

const INPUT =
	"mt-1 w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-base text-text-base focus:outline-none focus:ring-2 focus:ring-[#008782]";
const CARD = "rounded-xl border border-gray-200 bg-white p-[18px]";
const PRIMARY =
	"rounded-lg bg-[#004C4B] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#003a3a] disabled:opacity-50";

/** A year count is a whole number, or blank for no limit. */
const validYears = (value: string): boolean => value.trim() === "" || /^\d+$/.test(value.trim());
/** The months a fiscal year can end in: the roll-up of a model's quarters
 * and months into years reads it (Hackberry, 2026-10). */
const MONTHS = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
] as const;

interface ConventionsCardProps {
	conventions: Record<string, string>;
	onSave: (conventions: Record<string, string>) => Promise<boolean>;
}

/** One tone for every memo on the workflow. */
const ConventionsCard: React.FC<ConventionsCardProps> = ({ conventions, onSave }) => {
	const stored = conventions.tone ?? "";
	const [tone, setTone] = useState(stored);
	const [saving, setSaving] = useState(false);
	const toneId = useId();
	useEffect(() => {
		setTone(stored);
	}, [stored]);
	const dirty = tone.trim() !== stored;

	const save = async () => {
		if (!dirty || saving) return;
		setSaving(true);
		try {
			await onSave({ tone: tone.trim() });
		} finally {
			setSaving(false);
		}
	};

	return (
		<section className={CARD} aria-label="Memo conventions">
			<h2 className="text-[14px] font-semibold text-[#111111]">Voice</h2>
			<p className="mt-0.5 text-[12.5px] text-[#6B7280]">
				How every section is written, for example "Neutral and factual. No
				recommendation."
			</p>
			<label htmlFor={toneId} className="mt-3 block text-[12.5px] font-semibold text-[#3f4247]">
				Tone
			</label>
			<textarea
				id={toneId}
				value={tone}
				onChange={(event) => setTone(event.target.value)}
				rows={9}
				className={INPUT}
			/>
			<div className="mt-3 flex items-center gap-2">
				<button
					type="button"
					onClick={() => void save()}
					disabled={!dirty || saving}
					className={PRIMARY}
				>
					{saving ? "Saving…" : "Save tone"}
				</button>
			</div>
		</section>
	);
};

interface YearWindowFieldsProps {
	conventions: Record<string, string>;
	onSave: (conventions: Record<string, string>) => Promise<boolean>;
}

/**
 * The financial summary's year window, shown inside the Financial figures
 * card above its rows: how
 * many fiscal years before and after the latest actual year the table
 * carries (DOP-512). Stored as workflow conventions, like the tone.
 */
const YearWindowFields: React.FC<YearWindowFieldsProps> = ({ conventions, onSave }) => {
	const stored = {
		historical_years: conventions.historical_years ?? "",
		forecast_years: conventions.forecast_years ?? "",
		fiscal_year_end: conventions.fiscal_year_end || "December",
	};
	const [draft, setDraft] = useState(stored);
	const [saving, setSaving] = useState(false);
	const historicalId = useId();
	const forecastId = useId();
	const yearEndId = useId();
	useEffect(() => {
		setDraft({
			historical_years: conventions.historical_years ?? "",
			forecast_years: conventions.forecast_years ?? "",
			fiscal_year_end: conventions.fiscal_year_end || "December",
		});
	}, [conventions.historical_years, conventions.forecast_years, conventions.fiscal_year_end]);

	const changed = Object.fromEntries(
		(["historical_years", "forecast_years", "fiscal_year_end"] as const)
			.filter((key) => draft[key].trim() !== stored[key])
			.map((key) => [key, draft[key].trim()]),
	);
	const dirty = Object.keys(changed).length > 0;
	const valid = validYears(draft.historical_years) && validYears(draft.forecast_years);

	const save = async () => {
		if (!dirty || !valid || saving) return;
		setSaving(true);
		try {
			await onSave(changed);
		} finally {
			setSaving(false);
		}
	};

	return (
		<fieldset aria-label="Year window">
			<legend className="float-left w-full text-[13px] font-semibold text-[#111111]">
				Years in the financial table
			</legend>
			<p className="clear-both pt-0.5 text-[12.5px] text-[#6B7280]">
				Counted from the latest actual year in the files: this many past years,
				then that year, then this many forecast years. Leave blank for all.
			</p>
			<div className="mt-2 grid gap-3 sm:grid-cols-2">
				<div className="max-w-[14rem]">
					<label htmlFor={historicalId} className="text-[12.5px] font-semibold text-[#3f4247]">
						Historical years
					</label>
					<input
						id={historicalId}
						value={draft.historical_years}
						onChange={(event) => setDraft({ ...draft, historical_years: event.target.value })}
						inputMode="numeric"
						placeholder="all"
						aria-invalid={!validYears(draft.historical_years)}
						className={INPUT}
					/>
				</div>
				<div className="max-w-[14rem]">
					<label htmlFor={forecastId} className="text-[12.5px] font-semibold text-[#3f4247]">
						Forecast years
					</label>
					<input
						id={forecastId}
						value={draft.forecast_years}
						onChange={(event) => setDraft({ ...draft, forecast_years: event.target.value })}
						inputMode="numeric"
						placeholder="all"
						aria-invalid={!validYears(draft.forecast_years)}
						className={INPUT}
					/>
				</div>
				<div className="max-w-[14rem]">
					<label htmlFor={yearEndId} className="text-[12.5px] font-semibold text-[#3f4247]">
						Fiscal year ends in
					</label>
					<select
						id={yearEndId}
						value={draft.fiscal_year_end}
						onChange={(event) => setDraft({ ...draft, fiscal_year_end: event.target.value })}
						className={INPUT}
					>
						{MONTHS.map((month) => (
							<option key={month} value={month}>
								{month}
							</option>
						))}
					</select>
				</div>
			</div>
			<div className="mt-3 flex items-center gap-2">
				<button
					type="button"
					onClick={() => void save()}
					disabled={!dirty || !valid || saving}
					className={PRIMARY}
				>
					{saving ? "Saving…" : "Save year window"}
				</button>
				{!valid && (
					<span className="text-xs text-red-700">Years are whole numbers, or blank.</span>
				)}
			</div>
		</fieldset>
	);
};

const DealScreeningSettings: React.FC<DealScreeningSettingsProps> = ({
	workflow,
	onSaveRecipe,
}) => {
	const { recipe } = workflow;
	const sections = [...recipe.sections].sort(
		(a, b) => a.sort_order - b.sort_order,
	);
	const dealSpecific = [...recipe.deal_specific_sections].sort(
		(a, b) => a.sort_order - b.sort_order,
	);
	// One card open at a time, the first by default: a list of ten open
	// editors is a form nobody reads.
	const [expandedKey, setExpandedKey] = useState<string | null>(
		sections[0]?.key ?? null,
	);
	const toggle = (key: string) =>
		setExpandedKey((current) => (current === key ? null : key));

	const [savingKeys, setSavingKeys] = useState<ReadonlySet<string>>(
		() => new Set<string>(),
	);
	const savePolicy = useCallback(
		async (edits: DealScreeningRecipeEdits, keys: string[]) => {
			setSavingKeys((current) => new Set([...Array.from(current), ...keys]));
			try {
				await onSaveRecipe(edits);
			} finally {
				setSavingKeys((current) => {
					const next = new Set(current);
					keys.forEach((key) => next.delete(key));
					return next;
				});
			}
		},
		[onSaveRecipe],
	);

	const openDealSpecific = dealSpecific.find(
		(section) => section.key === expandedKey,
	);

	const [adding, setAdding] = useState(false);
	const [newTitle, setNewTitle] = useState("");
	const [newBrief, setNewBrief] = useState("");
	const [addSaving, setAddSaving] = useState(false);
	const canAdd = newTitle.trim() !== "" && newBrief.trim() !== "" && !addSaving;
	const addSection = async (event: React.FormEvent) => {
		event.preventDefault();
		if (!canAdd) return;
		setAddSaving(true);
		try {
			const ok = await onSaveRecipe({
				add_deal_specific_sections: [{ title: newTitle.trim(), brief: newBrief.trim() }],
			});
			if (ok) {
				setAdding(false);
				setNewTitle("");
				setNewBrief("");
			}
		} finally {
			setAddSaving(false);
		}
	};

	return (
		<div className="space-y-5">
			<p className="text-base text-[#5B6472]">
				These apply to every memo in this workflow. Changes show up the next time
				a memo is generated.
			</p>
			<div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
				<div className="min-w-0 space-y-5">
					<section aria-label="Memo sections">
						<h2 className="text-[14px] font-semibold text-[#111111]">
							What goes in every memo, in order
						</h2>
						<p className="mt-0.5 text-[12.5px] text-[#6B7280]">
							Open a section to change what it says or when it appears. The
							section titles stay the same on every memo.
						</p>
						<ol className="mt-3 overflow-hidden rounded-xl border border-gray-200 bg-white">
							{sections.map((section, index) => (
								<SectionBriefCard
									key={section.key}
									section={section}
									number={index + 1}
									expanded={expandedKey === section.key}
									onToggle={() => toggle(section.key)}
									onSave={(edit) => onSaveRecipe({ sections: { [section.key]: edit } })}
								/>
							))}
						</ol>
					</section>

					<section aria-label="Deal-specific sections">
						<h2 className="text-[14px] font-semibold text-[#111111]">
							Sections for certain deals
						</h2>
						<p className="mt-0.5 text-[12.5px] text-[#6B7280]">
							Extra sections Doppler adds only when a deal's files cover them.
							Which ones a deal may get is set in that deal's "…" menu.
						</p>
						<div className="mt-2.5 flex flex-wrap gap-1.5">
							{dealSpecific.map((section) => (
								<button
									key={section.key}
									type="button"
									onClick={() => toggle(section.key)}
									aria-pressed={expandedKey === section.key}
									className={`rounded-full border px-3 py-1 text-sm ${
										expandedKey === section.key
											? "border-[#004C4B] bg-[#E6F3F3] font-medium text-[#004C4B]"
											: "border-gray-200 bg-white text-text-strong hover:border-gray-300"
									} ${section.enabled ? "" : "line-through opacity-60"}`}
								>
									{section.title}
								</button>
							))}
							<button
								type="button"
								onClick={() => {
									setAdding((value) => !value);
									setExpandedKey(null);
								}}
								aria-expanded={adding}
								aria-label="Add a deal-specific section"
								className="rounded-full border border-dashed border-[#99CFCD] px-3 py-1 text-sm font-medium text-[#004C4B] hover:bg-[#f6fbfb]"
							>
								+ Add a section
							</button>
						</div>
						{adding && (
							<form
								onSubmit={(event) => void addSection(event)}
								className={`mt-3 space-y-3 ${CARD}`}
								aria-label="New deal-specific section"
							>
								<label className="block text-[12.5px] font-semibold text-[#3f4247]">
									Section title
									<input
										value={newTitle}
										onChange={(event) => setNewTitle(event.target.value)}
										placeholder="Royalty & Streaming Terms"
										className={`font-normal ${INPUT}`}
									/>
								</label>
								<label className="block text-[12.5px] font-semibold text-[#3f4247]">
									When it applies and what it says
									<textarea
										value={newBrief}
										onChange={(event) => setNewBrief(event.target.value)}
										rows={3}
										placeholder="The royalty rate, the ounces it applies to and any step-downs, as the agreement states them."
										className={`font-normal ${INPUT}`}
									/>
								</label>
								<div className="flex gap-2">
									<button type="submit" disabled={!canAdd} className={PRIMARY}>
										{addSaving ? "Adding…" : "Add section"}
									</button>
									<button
										type="button"
										onClick={() => setAdding(false)}
										disabled={addSaving}
										className="rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-sm font-medium text-text-strong disabled:opacity-50"
									>
										Cancel
									</button>
								</div>
							</form>
						)}
						{openDealSpecific && !adding && (
							<ul className="mt-3 overflow-hidden rounded-xl border border-gray-200 bg-white">
								<SectionBriefCard
									section={openDealSpecific}
									expanded
									onToggle={() => toggle(openDealSpecific.key)}
									onSave={(edit) =>
										onSaveRecipe({ sections: { [openDealSpecific.key]: edit } })
									}
								/>
							</ul>
						)}
					</section>
				</div>

				<ConventionsCard
					conventions={recipe.conventions}
					onSave={(conventions) => onSaveRecipe({ conventions })}
				/>
			</div>

			<RecipePolicyTables
				header={recipe.header}
				figures={recipe.figures}
				figuresExtra={
					<YearWindowFields
						conventions={recipe.conventions}
						onSave={(conventions) => onSaveRecipe({ conventions })}
					/>
				}
				savingKeys={savingKeys}
				onSave={(edits, keys) => void savePolicy(edits, keys)}
			/>
		</div>
	);
};

export default DealScreeningSettings;
